<script setup lang="ts">
import { ChevronLeft } from 'lucide-vue-next'
import { FunctionalComponent, HTMLAttributes, nextTick, ref, watch } from 'vue'
import { cn } from '../../lib/utils'
import HorizontalLine from '../HorizontalLine.vue'
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from '../ui/carousel'

export type ViewSwipePages = {
  id: string
  label?: string
  icon?: FunctionalComponent<any>
  hideHeader?: boolean
  skipSlideAnimation?: boolean
}

const props = defineProps<{
  views: ViewSwipePages[]
  backLabel?: string
  hideBackLabel?: boolean
  hideHeader?: boolean
  currentView: string | null
  class?: HTMLAttributes['class']
  allViewsInStack?: boolean

  skipSlideAnimation?: boolean
}>()
const emit = defineEmits<{
  (e: 'update:currentView', value: string | null): void
}>()
const api = ref<CarouselApi>()

const viewStack = ref<(string | null)[]>(
  props.allViewsInStack
    ? [null, ...props.views.map((view) => view.id)]
    : // Not optimal, but we need a
      [null]
)
const currentScroll = ref(0)
const initialHeight = ref<number | null>(null)
// const heightStyle = ref<string | null>(null)
const defaultHeight = '100%'
// const defaultHeight = props.allViewsInStack ? '100%' : '1000px'
const defaultTransitionStyle = 'height 0.1s ease-out'

const isInMotion = ref(false)
// const transitionStyle = ref<string>(defaultTransitionStyle)

const currentContainerHeight = ref<string | null>(null)
function getCurrentContainerElement() {
  return api.value?.containerNode().getElementsByClassName(getContainerClass(props.currentView))[0]
}
let currentContainerResizeObserver: ResizeObserver | null = null

function reattachResizeObserver() {
  const element = getCurrentContainerElement()
  if (!element) return
  currentContainerResizeObserver?.disconnect()
  currentContainerResizeObserver = new ResizeObserver(() => {
    currentContainerHeight.value = element?.clientHeight + 'px'
  })
  currentContainerResizeObserver.observe(element)
}

watch(
  () => props.currentView,
  async (newV) => {
    if (!api.value) return

    await gotoView(newV)
  },
  { immediate: true }
)

const containerStyle = ref<Record<string, string>>({
  // borderRight: '2px solid transparent',
  borderLeft: '2px solid transparent',
  // height: '100%',
  // minHeight: defaultHeight,
  transition: defaultTransitionStyle,
})

const settlePixelThreshold = 1

function onSettle() {
  // console.log('settle event')
  if (!isInMotion.value) return

  if (currentScroll.value < viewStack.value.length - 1 && !props.allViewsInStack) {
    // remove the later views from the stack
    viewStack.value = viewStack.value.slice(0, currentScroll.value + 1)
  }
  isInMotion.value = false
}

function isSettled() {
  const { dragHandler, location, target } = api.value!.internalEngine()
  if (dragHandler.pointerDown()) return false // Bail if user is dragging

  const displacement = target.get() - location.get()
  if (Math.abs(displacement) < settlePixelThreshold) return true

  return false
}

// function setCorrectContainerHeight() {
//   const containerElement = api.value
//     ?.containerNode()
//     .getElementsByClassName(getContainerClass(props.currentView))[0]

//   const containerElementHeight = containerElement?.clientHeight + 'px'
//   console.log(
//     'containerelement',
//     containerElement,
//     'containerElementHeight',
//     containerElementHeight
//   )
//   containerStyle.value = {
//     ...containerStyle.value,
//     height: containerElementHeight,
//   }
// }

function setApi(val: CarouselApi) {
  // console.log('setApi called', val)
  api.value = val!
  api.value.on('scroll', (ev) => {
    if (isSettled()) {
      onSettle()
      return
    }

    const currentScrollSnap = ev.selectedScrollSnap()
    // console.log('scroll event', ev, currentScrollSnap, currentScroll.value)
    if (currentScrollSnap === currentScroll.value) return
    currentScroll.value = currentScrollSnap
    isInMotion.value = true

    if (props.allViewsInStack) return

    if (currentScroll.value === 0) {
      containerStyle.value = {
        ...containerStyle.value,
        // minHeight: initialHeight.value ? `${initialHeight.value}px` : defaultHeight,
        // transition: 'height 0.5s ease-out',
        // borderLeft: '2px solid black',
      }
    } else {
      containerStyle.value = {
        ...containerStyle.value,
        // minHeight: defaultHeight,
        transition: defaultTransitionStyle,
        // borderLeft: '2px solid black',
      }
    }
    // setCorrectContainerHeight()
  })

  api.value.on('settle', () => {
    onSettle()
  })

  api.value.on('init', () => {
    // console.log('init event')
    initialHeight.value = api.value?.rootNode()?.offsetHeight || null
    // setCorrectContainerHeight()
    // if (props.currentView) {
    gotoView(props.currentView, true)
    // }
    if (props.allViewsInStack) return

    containerStyle.value = {
      ...containerStyle.value,
      // minHeight: initialHeight.value ? `${initialHeight.value}px` : defaultHeight,
    }
  })
}

async function gotoView(viewId: string | null, jump: boolean = false) {
  console.log('gotoView called with', viewId, jump)
  if (!api?.value) {
    // console.log('api not initialized')
    return
  }

  const shouldJump = props.views.find((view) => view.id === viewId)?.skipSlideAnimation || jump
  let newIndex = viewStack.value.findIndex((v) => v === viewId)
  // if the view does not exist in the list, we add it to the stack
  if (newIndex === -1) {
    const newScrollIndex = currentScroll.value + 1
    const copyStack = [...viewStack.value]
    copyStack[newScrollIndex] = viewId
    currentScroll.value = newScrollIndex

    viewStack.value = copyStack
    // console.log('new view added to stack', viewId, 'currentscroll', currentScroll.value)
    await nextTick()
    await nextTick()

    console.log('shouldJump for new view:', shouldJump)
    // setCorrectContainerHeight()
    api.value.scrollTo(viewStack.value.length - 1, shouldJump)
  } else {
    // setCorrectContainerHeight()
    api.value.scrollTo(newIndex, shouldJump)
  }
  reattachResizeObserver()
}

// watch(
//   () => props.currentView,
//   async (newV) => {
//     gotoView(newV)
//     // if (!api?.value) return
//     // let newIndex = viewStack.value.findIndex((v) => v === newV)
//     // // if the view does not exist in the list, we add it to the stack
//     // if (newIndex === -1) {
//     //   const copyStack = [...viewStack.value]
//     //   copyStack[currentScroll.value + 1] = newV
//     //   viewStack.value = copyStack
//     //   await nextTick()
//     //   api.value.scrollTo(viewStack.value.length)
//     // } else {
//     //   api.value.scrollTo(newIndex)
//     // }
//   }
//   // { immediate: true }
// )

const getViewInfo = (viewId: string | null) => {
  return props.views.find((view) => view.id === viewId)
}

const getContainerClass = (viewId: string | null) => {
  return '_pageSwipe_' + (viewId ?? '__root__')
}
</script>
<template>
  <Carousel
    @init-api="setApi"
    :class="cn('w-full ', { 'animate-border-blink': isInMotion })"
    :opts="{ watchDrag: false }"
    ignore-keys
    :style="{ ...containerStyle, height: currentContainerHeight }"
  >
    <CarouselContent class="h-fit">
      <!-- :key="view ?? '__root__'" -->
      <CarouselItem
        v-for="(view, index) in viewStack"
        :key="index"
        class="flex flex-col gap-2 h-fit"
        :class="getContainerClass(view)"
      >
        <div class="h-full w-full flex flex-col gap-2" :class="props.class">
          <template v-if="view !== null && !hideHeader && !getViewInfo(view)?.hideHeader">
            <div class="flex w-full justify-between items-center gap-4">
              <div
                v-if="!hideBackLabel"
                @click="emit('update:currentView', null)"
                class="flex items-center gap-1 cursor-pointer hover:underline"
              >
                <ChevronLeft :size="16" class="text-slate-500" />
                <span class="text-slate-500 text-xs font-medium">
                  {{ backLabel }}
                </span>
              </div>
              <slot name="header" />
              <h2 class="text-md font-semibold flex items-center gap-2 text-slate-700">
                <component :is="getViewInfo(view)?.icon" :size="16" class="text-slate-500 h-4" />
                {{ getViewInfo(view)?.label }}
              </h2>
            </div>
            <HorizontalLine class="w-full border-slate-200 mb-1 border-b" />
          </template>
          <div class="p-1 h-full" v-if="getViewInfo(view)?.id">
            <slot :name="getViewInfo(view)?.id" />
          </div>
          <slot v-else />
        </div>
      </CarouselItem>
      <!-- <CarouselItem
        v-if="viewStack.length <= 1"
        class="flex flex-col gap-2 h-full"
        :key="'__shadow'"
      >
      </CarouselItem> -->
    </CarouselContent>
  </Carousel>
</template>
